import {
  dropTargetForElements,
  draggable as makeDraggable
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import clsx from 'clsx'
import {
  createElement,
  type ReactElement,
  useEffect,
  useRef,
  useState
} from 'react'
import type { DashboardBlockProps } from '../../types'
import {
  blockDraggedOver,
  blockDragging,
  blockWrapper
} from './DashboardBlock.css'

export const DashboardBlock = ({
  id,
  type,
  title,
  icon,
  position,
  children,
  draggable = true,
  onReorder
}: DashboardBlockProps): ReactElement => {
  const ref = useRef<HTMLDivElement | null>(null)
  const dragHandleRef = useRef<HTMLHeadingElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  useEffect(() => {
    const element = ref.current
    const dragHandle = dragHandleRef.current
    if (!element) return

    const cleanupFunctions: Array<() => void> = []

    if (draggable && dragHandle) {
      const cleanupDragSource = makeDraggable({
        element: dragHandle,
        getInitialData: () => ({
          blockId: id,
          blockType: type,
          currentOrder: position
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false)
      })
      cleanupFunctions.push(cleanupDragSource)
    }

    const cleanupDropTarget = dropTargetForElements({
      element,
      getData: () => ({ blockId: id, blockType: type }),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false)
        const sourceBlockId = source.data.blockId as string

        if (sourceBlockId && sourceBlockId !== id && onReorder && position) {
          onReorder(sourceBlockId, position)
        }
      }
    })
    cleanupFunctions.push(cleanupDropTarget)

    return () => {
      cleanupFunctions.forEach((cleanup) => {
        cleanup()
      })
    }
  }, [id, type, position, draggable, onReorder])

  return (
    <div
      ref={ref}
      className={clsx(
        blockWrapper,
        isDraggedOver && blockDraggedOver,
        isDragging && blockDragging
      )}
      data-block-id={id}
      data-block-type={type}
    >
      {typeof children.type === 'function'
        ? createElement(children.type, {
            ...children.props,
            dragHandleRef: dragHandleRef,
            id,
            type,
            title,
            icon,
            position,
            draggable,
            onReorder
          })
        : children}
    </div>
  )
}
